import { useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText, Users, CalendarDays, Receipt, Pill, BarChart3, Bell, Globe,
  MessageSquare, ShieldCheck, Check, ArrowRight, Play, Sparkles, Star,
  Stethoscope, Languages, Award, HeartPulse, Lock, Database, KeyRound, Server, FileCheck2, Cloud, Search, Smartphone,
  Quote, Mic, Bot, Video, ClipboardList, TrendingUp, Activity, Megaphone, GraduationCap, BookOpen, Building2,
  ClipboardCheck, LayoutDashboard, Layers, MonitorSmartphone,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import doctorImg from "@/assets/landing-doctor.jpg";
import MegaMenu from "@/components/landing/MegaMenu";
import ProductTour from "@/components/landing/ProductTour";
import LivePreviews from "@/components/landing/LivePreviews";
import RoiAndCompare from "@/components/landing/RoiAndCompare";
import Testimonials from "@/components/landing/Testimonials";
import TestimonialsCarousel from "@/components/landing/TestimonialsCarousel";
import HowItWorks from "@/components/landing/HowItWorks";
import NewsletterSignup from "@/components/landing/NewsletterSignup";
import IntuitiveToolsSection from "@/components/landing/IntuitiveToolsSection";
import FourStepsSection from "@/components/landing/FourStepsSection";
import TrustStatBanner from "@/components/landing/TrustStatBanner";
import PlatformShowcase from "@/components/landing/PlatformShowcase";
import AudienceScaleSection from "@/components/landing/AudienceScaleSection";
import OutcomeStatsBento from "@/components/landing/OutcomeStatsBento";
import PortraitShowcase from "@/components/landing/PortraitShowcase";
import PageLivePulse from "@/components/public/PageLivePulse";
import PageLiveExamples from "@/components/public/PageLiveExamples";
import FaqSection from "@/components/landing/FaqSection";
import CaseStudies from "@/components/landing/CaseStudies";
import ScrollToTopFab from "@/components/landing/ScrollToTopFab";
import TrustAndIntegrations from "@/components/landing/TrustAndIntegrations";
import IntegrationsSetup from "@/components/landing/IntegrationsSetup";
import { useLang } from "@/contexts/LangContext";

import teamImg from "@/assets/landing-team.jpg";
import demoDashboard from "@/assets/demo/dashboard.png.asset.json";
import demoPatients from "@/assets/demo/patients.png.asset.json";
import demoPrescriptions from "@/assets/demo/prescriptions.png.asset.json";
import demoCompose from "@/assets/demo/compose.png.asset.json";
import StatusWidget from "@/components/StatusWidget";
import SecurityTrustSection from "@/components/landing/SecurityTrustSection";
import BottomInviteCta from "@/components/landing/BottomInviteCta";
import IntegrationsSection from "@/components/landing/IntegrationsSection";


/* Minimal SaaS — clean medical workspace.
   Locked tokens:
   - Canvas:    #ffffff  (white)
   - Surface:   #f8fafc  (gray-50)
   - Muted:     #f3f4f6  (gray-100)
   - Ink:       #111827  (gray-900)
   - Subtle:    #64748b  (gray-500)
   - Border:    #e2e8f0  (gray-200)
   - Accent:    #1ba179  (blue-600)
   Fonts:
   - Display:   Plus Jakarta Sans
   - Body:      Work Sans
   Bengali still uses Noto Sans Bengali as fallback.
*/

type Lang = "bn" | "en";
const isBnDefault = true;

const T = {
  nav: {
    features: { en: "Features", bn: "ফিচার" },
    workflow: { en: "How it works", bn: "কীভাবে কাজ করে" },
    pricing: { en: "Pricing", bn: "প্রাইসিং" },
    faq: { en: "FAQ", bn: "প্রশ্নোত্তর" },
    signin: { en: "Sign in", bn: "লগইন" },
    cta: { en: "Start free", bn: "ফ্রি শুরু করুন" },
  },
  hero: {
    badge: { en: "The new standard for BD doctors", bn: "বাংলাদেশী ডাক্তারদের নতুন মান" },
    h1a: { en: "Elevate your", bn: "আপনার প্র্যাকটিসকে" },
    h1b: { en: "Practice", bn: "উন্নত করুন" },
    h1c: { en: ".", bn: "।" },
    sub: {
      en: "A premium bilingual SaaS ecosystem for Bangladesh's leading physicians. Digital prescriptions, patient records, and হিসাব খাতা — one calm workspace.",
      bn: "বাংলাদেশের শীর্ষ চিকিৎসকদের জন্য প্রিমিয়াম দ্বিভাষিক SaaS। ডিজিটাল প্রেসক্রিপশন, রোগীর রেকর্ড এবং হিসাব খাতা — একটাই শান্ত ওয়ার্কস্পেস।",
    },
    ctaPrimary: { en: "Start your digital chamber", bn: "ডিজিটাল চেম্বার শুরু করুন" },
    ctaSecondary: { en: "Watch demo", bn: "ডেমো দেখুন" },
    rxTitle: { en: "Smart prescriptions", bn: "স্মার্ট প্রেসক্রিপশন" },
    rxSub: { en: "30-second complete Rx", bn: "৩০ সেকেন্ডে সম্পূর্ণ Rx" },
    waTitle: { en: "WhatsApp automation", bn: "হোয়াটসঅ্যাপ অটোমেশন" },
    waDesc: { en: "Automated reminders and follow-ups sent directly to patients.", bn: "রোগীদের কাছে স্বয়ংক্রিয় রিমাইন্ডার ও ফলো-আপ।" },
    billTitle: { en: "হিসাব খাতা", bn: "হিসাব খাতা" },
    billSub: { en: "Today", bn: "আজ" },
    billValue: { en: "৳ 15,450", bn: "৳ ১৫,৪৫০" },
    verifiedRx: { en: "Verified Rx", bn: "যাচাইকৃত Rx" },
    servedToday: { en: "Served today", bn: "আজ পরিসেবা" },
    servedValue: { en: "1,248 patients", bn: "১,২৪৮ রোগী" },
  },
  stats: [
    { icon: Stethoscope, value: { en: "500+", bn: "৫০০+" }, label: { en: "Registered doctors", bn: "নিবন্ধিত চিকিৎসক" }, hint: { en: "Across 40+ cities", bn: "৪০+ শহরে" }, live: false },
    { icon: Users, value: { en: "120k+", bn: "১.২ লাখ+" }, label: { en: "Patient records", bn: "রোগীর রেকর্ড" }, hint: { en: "Managed securely", bn: "নিরাপদে সংরক্ষিত" }, live: false },
    { icon: HeartPulse, value: { en: "99.9%", bn: "৯৯.৯%" }, label: { en: "Uptime SLA", bn: "আপটাইম SLA" }, hint: { en: "Live & operational", bn: "লাইভ চলছে" }, live: true },
    { icon: Star, value: { en: "4.9★", bn: "৪.৯★" }, label: { en: "Doctor satisfaction", bn: "চিকিৎসক সন্তুষ্টি" }, hint: { en: "From 1,200+ reviews", bn: "১,২০০+ রিভিউ" }, live: false },
  ] as const,
  features: {
    kicker: { en: "Comprehensive suite", bn: "সম্পূর্ণ সুইট" },
    title: { en: "Twelve tools. One workspace.", bn: "বারোটি টুল। একটাই ওয়ার্কস্পেস।" },
    sub: {
      en: "Every capability a modern Bangladeshi practice needs — clinical, financial, and growth — thoughtfully localized.",
      bn: "একটি আধুনিক বাংলাদেশী প্র্যাকটিসের সব প্রয়োজন — ক্লিনিক্যাল, আর্থিক ও গ্রোথ — যত্নে দ্বিভাষিক।",
    },
    items: [
      { icon: FileText, en: ["Digital prescriptions", "AI-assisted Rx with 50+ BD medicines and templates."], bn: ["ডিজিটাল প্রেসক্রিপশন", "৫০+ বাংলাদেশী ওষুধ ও টেমপ্লেটসহ AI-Rx।"] },
      { icon: Users, en: ["Patient records", "Vitals, risk score, family & full history — one screen."], bn: ["পেশেন্ট রেকর্ড", "ভাইটালস, রিস্ক স্কোর ও পূর্ণ ইতিহাস।"] },
      { icon: CalendarDays, en: ["Appointments & queue", "Kiosk check-in and live waiting queue."], bn: ["অ্যাপয়েন্টমেন্ট ও কিউ", "কিয়স্ক চেক-ইন ও লাইভ কিউ।"] },
      { icon: Receipt, en: ["Billing & হিসাব খাতা", "bKash / Nagad / Card with bilingual invoices."], bn: ["বিলিং ও হিসাব খাতা", "বিকাশ / নগদ / কার্ড ও দ্বিভাষিক ইনভয়েস।"] },
      { icon: Pill, en: ["Pharmacy & inventory", "Stock, expiry and reorder alerts."], bn: ["ফার্মেসি ও ইনভেন্টরি", "স্টক, মেয়াদ ও রি-অর্ডার অ্যালার্ট।"] },
      { icon: BarChart3, en: ["Analytics & reports", "Daily income register and chamber-wise reports."], bn: ["অ্যানালিটিক্স ও রিপোর্ট", "দৈনিক আয় ও চেম্বার-ভিত্তিক রিপোর্ট।"] },
      { icon: Bell, en: ["Recalls & reminders", "Auto follow-ups, birthdays, vaccination alerts."], bn: ["রিকল ও রিমাইন্ডার", "স্বয়ংক্রিয় ফলো-আপ ও টিকাদান অ্যালার্ট।"] },
      { icon: Globe, en: ["Public profile & website", "Your /dr/name page with articles and reviews."], bn: ["পাবলিক প্রোফাইল ও ওয়েবসাইট", "আর্টিকেল ও রিভিউসহ /dr/name পেজ।"] },
      { icon: MessageSquare, en: ["Messaging templates", "SMS + WhatsApp templates with variables."], bn: ["মেসেজিং টেমপ্লেট", "SMS + হোয়াটসঅ্যাপ ভেরিয়েবল টেমপ্লেট।"] },
      { icon: Video, en: ["Teleconsultation", "One-click secure video visits with prescription in the same flow."], bn: ["টেলিকনসাল্টেশন", "সিকিউর ভিডিও ভিজিট ও একই ফ্লোতে প্রেসক্রিপশন।"] },
      { icon: ClipboardCheck, en: ["Medical certificates", "Fitness, sick-leave and disability certificates with MC refs."], bn: ["মেডিকেল সার্টিফিকেট", "ফিটনেস, অসুস্থতা ও প্রতিবন্ধকতা সার্টিফিকেট।"] },
      { icon: Building2, en: ["Multi-chamber & staff", "Manage chambers, schedules, roles and staff in one account."], bn: ["মাল্টি-চেম্বার ও স্টাফ", "চেম্বার, সিডিউল, রোল ও স্টাফ একই অ্যাকাউন্টে।"] },
    ] as const,
  },
  aiSuite: {
    kicker: { en: "AI & Growth Suite", bn: "AI ও গ্রোথ সুইট" },
    title: { en: "AI that runs your practice while you see patients.", bn: "AI যা রোগী দেখার সময় আপনার প্র্যাকটিস চালায়।" },
    sub: {
      en: "Eight autonomous modules — from Bengali ambient scribing to WhatsApp booking agents — designed to reclaim hours of admin work every day.",
      bn: "আটটি স্বয়ংক্রিয় মডিউল — বাংলা অ্যাম্বিয়েন্ট স্ক্রাইব থেকে হোয়াটসঅ্যাপ বুকিং এজেন্ট — প্রতিদিন ঘণ্টার পর ঘণ্টা অ্যাডমিন কাজ কমায়।",
    },
    items: [
      { slug: "ambient-scribe", icon: Mic, tag: { en: "Clinical AI", bn: "ক্লিনিক্যাল AI" }, en: ["Ambient Bengali scribe", "Record the visit — get structured Bengali SOAP notes saved to the chart automatically."], bn: ["অ্যাম্বিয়েন্ট বাংলা স্ক্রাইব", "ভিজিট রেকর্ড করুন — স্ট্রাকচার্ড বাংলা SOAP নোট চার্টে অটো-সেভ।"] },
      { slug: "whatsapp-agent", icon: Bot, tag: { en: "24/7 Agent", bn: "২৪/৭ এজেন্ট" }, en: ["WhatsApp booking agent", "Answers patients, books, reschedules, cancels appointments — with staff escalation."], bn: ["হোয়াটসঅ্যাপ বুকিং এজেন্ট", "রোগীদের উত্তর, বুকিং, রিশিডিউল ও ক্যানসেল — স্টাফ এস্কালেশনসহ।"] },
      { slug: "care-gaps", icon: Activity, tag: { en: "Population Health", bn: "পপুলেশন হেলথ" }, en: ["Care-gap engine", "Detects overdue HbA1c, BP, TSH, screenings — drafts personalized bilingual reminders."], bn: ["কেয়ার-গ্যাপ ইঞ্জিন", "ওভারডিউ HbA1c, BP, TSH ও স্ক্রিনিং শনাক্ত — ব্যক্তিগত দ্বিভাষিক রিমাইন্ডার।"] },
      { slug: "specialty-templates", icon: Layers, tag: { en: "Specialty Packs", bn: "স্পেশালিটি প্যাক" }, en: ["Specialty templates", "One-click OB/GYN, T2DM, HTN, PCOS packs fill diagnosis, meds, notes, follow-up."], bn: ["স্পেশালিটি টেমপ্লেট", "এক-ক্লিকে OB/GYN, T2DM, HTN, PCOS প্যাক — সব ফিল্ড অটো-ফিল।"] },
      { slug: "nps", icon: TrendingUp, tag: { en: "Reputation", bn: "রেপুটেশন" }, en: ["NPS & satisfaction", "Auto-send post-visit surveys — full reputation dashboard with trends & comments."], bn: ["NPS ও সন্তুষ্টি", "পোস্ট-ভিজিট সার্ভে অটো — ট্রেন্ড ও কমেন্টসহ রেপুটেশন ড্যাশবোর্ড।"] },
      { slug: "diagnosis-ai", icon: Sparkles, tag: { en: "Clinical AI", bn: "ক্লিনিক্যাল AI" }, en: ["Diagnosis & drug safety", "AI diagnosis suggestions, treatment plans and real-time drug-interaction checks."], bn: ["ডায়াগনসিস ও ড্রাগ সেফটি", "AI ডায়াগনসিস, ট্রিটমেন্ট প্ল্যান ও রিয়েল-টাইম ইন্টারঅ্যাকশন চেক।"] },
      { slug: "campaigns", icon: Megaphone, tag: { en: "Growth", bn: "গ্রোথ" }, en: ["Campaigns & acquisition", "WhatsApp/SMS campaigns, waitlist, patient acquisition analytics."], bn: ["ক্যাম্পেইন ও অ্যাকুইজিশন", "হোয়াটসঅ্যাপ/SMS ক্যাম্পেইন, ওয়েটলিস্ট ও অ্যাকুইজিশন অ্যানালিটিক্স।"] },
      { slug: "desktop-pwa", icon: MonitorSmartphone, tag: { en: "Desktop + PWA", bn: "ডেস্কটপ + PWA" }, en: ["Works offline, everywhere", "Windows / macOS / Linux desktop app, PWA, and encrypted login — even without internet."], bn: ["সব জায়গায় অফলাইন কাজ করে", "Windows / macOS / Linux ডেস্কটপ, PWA ও এনক্রিপ্টেড লগইন — ইন্টারনেট ছাড়াও।"] },
    ] as const,
  },
  workflow: {
    kicker: { en: "A day in your practice", bn: "প্র্যাকটিসের একটি দিন" },
    title: { en: "From walk-in to follow-up.", bn: "আগমন থেকে ফলো-আপ পর্যন্ত।" },
    steps: [
      { en: ["Check-in", "Kiosk or online booking with 8-min ETA."], bn: ["চেক-ইন", "কিয়স্ক বা অনলাইন বুকিং, ৮-মিনিট ETA।"] },
      { en: ["Consult", "Full history, vitals and risk on one screen."], bn: ["কনসাল্টেশন", "পূর্ণ ইতিহাস, ভাইটালস ও রিস্ক।"] },
      { en: ["Prescribe", "Type-ahead meds, AI diagnosis, one-click invoice."], bn: ["প্রেসক্রিপশন", "টাইপ-অ্যাহেড, AI ডায়াগনসিস, এক-ক্লিক বিল।"] },
      { en: ["Follow-up", "WhatsApp reminders, recalls and reviews."], bn: ["ফলো-আপ", "হোয়াটসঅ্যাপ রিমাইন্ডার ও রিভিউ।"] },
    ] as const,
  },
  liveDemo: {
    highlights: {
      kicker: { en: "Why doctors switch", bn: "ডাক্তাররা কেন স্যুইচ করেন" },
      title: { en: "Patient care, faster.", bn: "রোগী সেবা, দ্রুততর।" },
      items: [
        {
          icon: Users,
          en: ["All-in-one patient records", "Vitals, history, risk score and visits in one calm screen."],
          bn: ["অল-ইন-ওয়ান রোগী রেকর্ড", "ভাইটালস, ইতিহাস, রিস্ক স্কোর ও সব ভিজিট এক স্ক্রিনে।"],
        },
        {
          icon: Search,
          en: ["Instant patient lookup", "Search by name, phone or condition — records appear in milliseconds."],
          bn: ["তাৎক্ষণিক রোগী সার্চ", "নাম, ফোন বা রোগ দিয়ে সার্চ — রেকর্ড মিলিসেকেন্ডে।"],
        },
        {
          icon: FileText,
          en: ["30-second prescriptions", "Templates, quick-add and auto-dose — finish Rx before the patient leaves."],
          bn: ["৩০ সেকেন্ডে প্রেসক্রিপশন", "টেমপ্লেট, কুইক-অ্যাড ও অটো-ডোজ — রোগী যাওয়ার আগেই শেষ।"],
        },
        {
          icon: Pill,
          en: ["Smart dosage autocomplete", "Type 'napa 500 1+0+1 5d' and the system fills the full instruction."],
          bn: ["স্মার্ট ডোজ অটোকমপ্লিট", "'napa 500 1+0+1 5d' লিখলেই পুরো নির্দেশনা তৈরি।"],
        },
        {
          icon: Smartphone,
          en: ["Share Rx via QR or WhatsApp", "Send a clear, bilingual prescription directly to the patient."],
          bn: ["QR বা হোয়াটসঅ্যাপে Rx শেয়ার", "স্পষ্ট দ্বিভাষিক প্রেসক্রিপশন রোগীকে পাঠান।"],
        },
        {
          icon: Bell,
          en: ["Automatic follow-ups", "Birthday, recall and reminder alerts keep patients coming back."],
          bn: ["স্বয়ংক্রিয় ফলো-আপ", "জন্মদিন, রিকল ও রিমাইন্ডার রোগীদের ফিরিয়ে আনে।"],
        },
      ] as const,
    },
  },
  pricing: {

    kicker: { en: "Pricing", bn: "প্রাইসিং" },
    title: { en: "Fair prices, in taka.", bn: "ন্যায্য দাম, টাকায়।" },
    sub: { en: "No hidden fees. Cancel anytime.", bn: "কোনো লুকানো ফি নেই। যেকোনো সময় বাতিল।" },
    popular: { en: "Best choice", bn: "সেরা পছন্দ" },
    plans: [
      {
        name: { en: "Starter", bn: "স্টার্টার" },
        price: { en: "0", bn: "০" },
        priceSuffix: { en: "forever", bn: "চিরকাল" },
        tagline: { en: "Try it free", bn: "বিনামূল্যে ব্যবহার" },
        features: {
          en: ["1 chamber", "50 patients / mo", "Digital Rx", "Basic analytics"],
          bn: ["১টি চেম্বার", "৫০ রোগী / মাস", "ডিজিটাল Rx", "বেসিক অ্যানালিটিক্স"],
        },
        cta: { en: "Get started", bn: "শুরু করুন" },
        highlight: false,
      },
      {
        name: { en: "Practice", bn: "প্র্যাকটিস" },
        price: { en: "1,999", bn: "১,৯৯৯" },
        priceSuffix: { en: "/ month", bn: "/ মাস" },
        tagline: { en: "For solo doctors", bn: "একক চিকিৎসকদের জন্য" },
        features: {
          en: ["3 chambers", "Unlimited patients", "AI diagnosis & risk", "WhatsApp reminders", "Public profile"],
          bn: ["৩টি চেম্বার", "আনলিমিটেড রোগী", "AI ডায়াগনসিস", "হোয়াটসঅ্যাপ রিমাইন্ডার", "পাবলিক প্রোফাইল"],
        },
        cta: { en: "Start 14-day trial", bn: "১৪-দিন ট্রায়াল" },
        highlight: true,
      },
      {
        name: { en: "Pro", bn: "প্রো" },
        price: { en: "3,499", bn: "৩,৪৯৯" },
        priceSuffix: { en: "/ month", bn: "/ মাস" },
        tagline: { en: "For busy doctors", bn: "ব্যস্ত ডাক্তারদের জন্য" },
        features: {
          en: ["Unlimited chambers", "AI ambient scribe", "WhatsApp booking agent", "Priority Bangla support", "Custom domain"],
          bn: ["আনলিমিটেড চেম্বার", "AI অ্যাম্বিয়েন্ট স্ক্রাইব", "হোয়াটসঅ্যাপ বুকিং এজেন্ট", "প্রায়োরিটি সাপোর্ট", "কাস্টম ডোমেইন"],
        },
        cta: { en: "Start 14-day trial", bn: "১৪-দিন ট্রায়াল" },
        highlight: false,
      },
    ] as const,
  },
  reviews: {
    kicker: { en: "Loved by doctors", bn: "চিকিৎসকদের প্রিয়" },
    title: { en: "Real practices. Real time saved.", bn: "সত্যিকারের প্র্যাকটিস। সত্যিকারের সময় বাঁচে।" },
    items: [
      { name: { en: "Dr. Rahman", bn: "ডাঃ রহমান" }, role: { en: "Cardiologist · Dhaka", bn: "কার্ডিওলজিস্ট · ঢাকা" }, quote: { en: "Cut my paperwork in half. Bengali invoices are a game-changer for my patients.", bn: "কাগজপত্রের কাজ অর্ধেক কমেছে। বাংলা ইনভয়েস রোগীদের জন্য অসাধারণ।" } },
      { name: { en: "Dr. Sultana", bn: "ডাঃ সুলতানা" }, role: { en: "Pediatrician · Chittagong", bn: "শিশু বিশেষজ্ঞ · চট্টগ্রাম" }, quote: { en: "Queue kiosk and 8-minute wait keep my chamber organized without any staff.", bn: "কিউ কিয়স্ক ও ৮-মিনিট ওয়েট স্টাফ ছাড়াই চেম্বার সাজানো রাখে।" } },
      { name: { en: "Dr. Karim", bn: "ডাঃ করিম" }, role: { en: "GP · Sylhet", bn: "জেনারেল ফিজিশিয়ান · সিলেট" }, quote: { en: "AI diagnoses catch things I would have missed. Worth every taka.", bn: "AI-এর ডায়াগনসিস অনেক কিছু ধরিয়ে দেয়। প্রতিটি টাকা সার্থক।" } },
      { name: { en: "Dr. Nusrat", bn: "ডাঃ নুসরাত" }, role: { en: "Dermatologist · Rajshahi", bn: "ডার্মাটোলজিস্ট · রাজশাহী" }, quote: { en: "My patients love the WhatsApp reminders. Follow-ups jumped by 40% in two months.", bn: "রোগীরা হোয়াটসঅ্যাপ রিমাইন্ডার পছন্দ করে। দুই মাসে ফলো-আপ ৪০% বেড়েছে।" } },
    ] as const,
  },
  faq: {
    title: { en: "Questions, answered.", bn: "প্রশ্ন? উত্তর এখানে।" },
    items: [
      { q: { en: "Is my patient data secure?", bn: "রোগীর তথ্য কি নিরাপদ?" }, a: { en: "Yes — encrypted at rest and in transit, isolated per doctor with row-level security.", bn: "হ্যাঁ — সম্পূর্ণ এনক্রিপ্টেড, প্রতি চিকিৎসকের জন্য পৃথক row-level security।" } },
      { q: { en: "Can I use DoctorsX in Bengali?", bn: "বাংলায় ব্যবহার করা যাবে?" }, a: { en: "The whole app, prescriptions, and invoices are fully bilingual using Noto Sans Bengali.", bn: "পুরো অ্যাপ, প্রেসক্রিপশন ও ইনভয়েস সম্পূর্ণ দ্বিভাষিক।" } },
      { q: { en: "Do I need to install anything?", bn: "কিছু ইনস্টল করতে হবে?" }, a: { en: "No — runs in your browser on desktop, tablet and phone.", bn: "না — ব্রাউজারেই চলে, ডেস্কটপ / ট্যাবলেট / ফোনে।" } },
      { q: { en: "BMDC number and signature?", bn: "বিএমডিসি ও সিগনেচার?" }, a: { en: "Save your BMDC registration and upload a digital signature used on every Rx.", bn: "বিএমডিসি নম্বর সেভ ও ডিজিটাল সিগনেচার আপলোড করুন।" } },
      { q: { en: "Can staff use it too?", bn: "স্টাফরা ব্যবহার করতে পারবে?" }, a: { en: "Yes — Practice and Pro plans include assistant accounts for your chamber.", bn: "হ্যাঁ — Practice ও Pro প্ল্যানে অ্যাসিস্ট্যান্ট অ্যাকাউন্ট।" } },
      { q: { en: "Can I import existing patients?", bn: "আগের রোগী ইমপোর্ট করা যাবে?" }, a: { en: "Yes — bulk CSV import on all plans.", bn: "হ্যাঁ — সব প্ল্যানে CSV ইমপোর্ট।" } },
    ] as const,
  },
  cta: {
    title: { en: "Is your practice still analog?", bn: "আপনার প্র্যাকটিস কি এখনো এনালগ?" },
    sub: { en: "Join DoctorsX today and give your patients a modern experience.", bn: "আজই DoctorsX-এ যোগ দিন এবং রোগীদের দিন আধুনিক অভিজ্ঞতা।" },
    primary: { en: "Start free", bn: "ফ্রি শুরু করুন" },
    secondary: { en: "Book a demo", bn: "ডেমো বুক করুন" },
  },
  footer: {
    tagline: { en: "The quiet operating system for Bangladeshi doctors. Built with care in Dhaka.", bn: "বাংলাদেশী চিকিৎসকদের জন্য নিরব অপারেটিং সিস্টেম। ঢাকায় যত্নে তৈরি।" },
    product: { en: "Product", bn: "প্রোডাক্ট" },
    company: { en: "Company", bn: "কোম্পানি" },
    legal: { en: "Legal", bn: "লিগ্যাল" },
    rights: { en: "All rights reserved.", bn: "সর্বস্বত্ব সংরক্ষিত।" },
  },
};

const t = (lang: Lang, node: { en: string; bn: string }) => node[lang];

/* Palette */
const C = {
  cream: "#f6f3ee",
  cream2: "#ffffff",
  deep: "#1a1a1a",
  emerald: "#25c39a",
  gold: "#25c39a",
  goldSoft: "#e9e4f5",
};


const Landing = () => {
  const { lang, setLang } = useLang();
  const [demoStep, setDemoStep] = useState(0);
  const [signupOpen, setSignupOpen] = useState(false);
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPhone, setSignupPhone] = useState("");
  const isBn = lang === "bn";
  void setLang; // header LangToggle drives this; local usages remain for compatibility


  const scrollToPricingAndOpenSignup = () => {
    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => setSignupOpen(true), 600);
  };

  const handleSignupSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    window.location.href = `/auth?email=${encodeURIComponent(signupEmail)}&phone=${encodeURIComponent(signupPhone)}`;
  };

  const bodyFont = isBn
    ? "'Noto Sans Bengali', 'DM Sans', system-ui, sans-serif"
    : "'DM Sans', system-ui, sans-serif";
  const displayFont = isBn
    ? "'Noto Sans Bengali', 'Space Grotesk', system-ui, sans-serif"
    : "'Space Grotesk', system-ui, sans-serif";

  const Serif = ({ className = "", italic = true, children }: { className?: string; italic?: boolean; children: React.ReactNode }) => (
    <span
      style={{
        fontFamily: displayFont,
        fontStyle: "normal",
        fontWeight: italic ? 700 : 600,
        letterSpacing: "-0.02em",
      }}
      className={className}
    >
      {children}
    </span>
  );

  const Kicker = ({ children }: { children: React.ReactNode }) => (
    <div className="inline-flex items-center gap-2 mb-6">
      <span className="w-8 h-px bg-[#25c39a]/30" />
      <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#6a6a6a]">{children}</span>
    </div>
  );

  const Logo = () => (
    <Link to="/" className="flex items-center gap-2.5 group">
      <div className="w-9 h-9 rounded-full bg-[#25c39a] flex items-center justify-center shadow-[0_6px_16px_-6px_rgba(37,195,154,0.55)] transition-transform group-hover:scale-105">
        <Stethoscope className="w-4 h-4 text-white" strokeWidth={2.5} />
      </div>
      <span
        style={{ fontFamily: displayFont, fontStyle: "normal", fontWeight: 700 }}
        className="text-[22px] leading-none tracking-tight text-[#1a1a1a]"
      >
        DoctorsX
      </span>
    </Link>

  );


  const LangToggle = () => (
    <div className="flex items-center bg-[#f8fafc] rounded-full p-0.5 border border-[#e2e8f0]">
      <button
        onClick={() => setLang("bn")}
        aria-pressed={isBn}
        className={`px-3 py-1 text-[11px] font-semibold rounded-full transition ${
          isBn ? "bg-[#111827] text-white" : "text-[#111827]/60"
        }`}
        style={{ fontFamily: "'Noto Sans Bengali', sans-serif" }}
      >
        বাংলা
      </button>
      <button
        onClick={() => setLang("en")}
        aria-pressed={!isBn}
        className={`px-3 py-1 text-[11px] font-semibold rounded-full transition ${
          !isBn ? "bg-[#111827] text-white" : "text-[#111827]/60"
        }`}
      >
        EN
      </button>
    </div>
  );

  return (
    <div
      lang={lang}
      className="min-h-screen text-[#0f172a] selection:bg-[#1ba179]/20"
      style={{ fontFamily: bodyFont, backgroundColor: C.cream }}
    >
      {/* ============ ANNOUNCEMENT BAR ============ */}
      <div className="w-full bg-[#1a1a1a] text-white">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center gap-2.5 text-[12px]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#25c39a] animate-pulse" />
          <span className="text-white/70">
            {isBn ? "নতুন — বাংলা প্রেসক্রিপশন AI" : "New — Bengali prescription AI is live"}
          </span>
          <Link
            to="/auth"
            className="inline-flex items-center gap-1 text-white/90 hover:text-white transition font-medium"
          >
            {isBn ? "চেষ্টা করুন" : "Try it"} <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      <div className="relative px-4 md:px-6 pt-4 md:pt-6">
        {/* ============ NAV ============ */}
        <div className="sticky top-2 md:top-3 z-50 mb-16 md:mb-24">
          <MegaMenu
            logo={<Logo />}
            ctaLabel={t(lang, T.nav.cta)}
            signInLabel={t(lang, T.nav.signin)}
            right={<LangToggle />}
          />
        </div>

        {/* ============ HERO — Serene OS ============ */}
        <main className="max-w-7xl mx-auto">
          <section className="relative pt-6 pb-24 md:pt-12 md:pb-36">
            <div className="max-w-4xl mx-auto text-center flex flex-col items-center gap-8 md:gap-10">
              {/* lavender eyebrow pill */}
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#e9e4f5] text-[#1a1a1a] text-[11px] font-medium tracking-[0.14em] uppercase">
                {isBn ? "নতুন: ইন্টিগ্রেটেড ক্লিনিক্যাল ওয়ার্কফ্লো" : "New: Integrated clinical workflows"}
              </div>

              {/* headline */}
              <h1
                className="text-[40px] sm:text-[54px] md:text-[72px] leading-[1.05] tracking-[-0.03em] font-semibold text-[#1a1a1a]"
                style={{ fontFamily: displayFont }}
              >
                {isBn ? (
                  <>
                    ডাক্তারদের জন্য আধুনিক{" "}
                    <span className="text-[#25c39a]">অপারেটিং সিস্টেম</span>।
                  </>
                ) : (
                  <>
                    DoctorsX: the modern{" "}
                    <span className="text-[#25c39a]">operating system</span> for doctors.
                  </>
                )}
              </h1>

              {/* sub */}
              <p className="max-w-xl mx-auto text-[17px] md:text-[19px] text-[#5a5a5a] leading-relaxed">
                {isBn
                  ? "প্র্যাকটিস ম্যানেজমেন্ট নতুনভাবে। পেশাদার টুলস — যেন কাগজপত্র নয়, রোগীর যত্নেই মনোযোগ থাকে।"
                  : "Practice management reimagined. Professional tools that feel human, designed to help you focus on patient care, not paperwork."}
              </p>

              {/* CTAs — rounded-full, black + white */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                <Link
                  to="/auth"
                  className="px-8 py-4 bg-[#1a1a1a] text-white rounded-full font-medium hover:opacity-90 transition-all text-[15px]"
                >
                  {isBn ? "ফ্রি ট্রায়াল শুরু করুন" : "Start your free trial"}
                </Link>
                <button
                  onClick={() => document.getElementById("live-demo")?.scrollIntoView({ behavior: "smooth" })}
                  className="px-8 py-4 bg-white border border-[#e0ddd8] text-[#1a1a1a] rounded-full font-medium hover:bg-[#f0ede8] transition-all text-[15px]"
                >
                  {isBn ? "ডেমো বুক করুন" : "Book a demo"}
                </button>
              </div>

              <div className="flex items-center justify-center gap-2 text-[12px] text-[#6a6a6a]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#25c39a]" />
                {isBn ? "কার্ড লাগবে না · ১ মিনিটে সেটআপ" : "No credit card · 1-minute setup"}
              </div>
            </div>

            {/* ============ FLOATING UI COMPOSITION ============ */}
            <div className="relative w-full max-w-3xl mx-auto mt-16 md:mt-20">
              {/* main platform card */}
              <div className="bg-white rounded-2xl border border-black/[0.06] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.08)] p-6 md:p-8 text-left">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#25c39a] flex items-center justify-center text-white font-bold text-sm" style={{ fontFamily: displayFont }}>
                      DX
                    </div>
                    <div>
                      <div className="text-[13px] font-semibold text-[#1a1a1a]">DoctorsX Platform</div>
                      <div className="text-[11px] text-[#9a9a9a]">v2.4 · Clinical Dashboard</div>
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#f0ede8]" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#f0ede8]" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#f0ede8]" />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 md:gap-6">
                  <div className="col-span-2 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="h-3 w-1/3 bg-[#f0ede8] rounded" />
                      <span className="text-[10px] font-semibold text-[#25c39a] uppercase tracking-wider">Live</span>
                    </div>
                    <div className="h-24 w-full bg-[#f6f3ee] rounded-xl flex items-center justify-center px-4">
                      <div className="w-full h-2 bg-[#25c39a]/15 rounded-full overflow-hidden">
                        <div className="h-full bg-[#25c39a] w-[68%]" />
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="h-8 w-24 bg-[#f0ede8] rounded-lg" />
                      <div className="h-8 w-24 bg-[#f0ede8] rounded-lg" />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="text-[10px] font-semibold text-[#9a9a9a] uppercase tracking-wider">Today</div>
                    <div className="h-3 w-full bg-[#f0ede8] rounded" />
                    <div className="h-3 w-5/6 bg-[#f0ede8] rounded" />
                    <div className="h-3 w-4/6 bg-[#f0ede8] rounded" />
                    <div className="pt-2">
                      <div className="text-[18px] font-bold text-[#1a1a1a]" style={{ fontFamily: displayFont }}>৳ 15,450</div>
                      <div className="text-[10px] text-[#9a9a9a]">হিসাব খাতা</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* floating lavender overlay — top right */}
              <div className="absolute -top-6 -right-4 md:-right-12 bg-white p-3.5 rounded-xl shadow-[0_16px_40px_-12px_rgba(0,0,0,0.15)] border border-black/[0.04] flex items-center gap-3 animate-fade-in">
                <div className="w-10 h-10 bg-[#e9e4f5] rounded-full flex items-center justify-center">
                  <ClipboardList className="w-5 h-5 text-[#7c5eb1]" />
                </div>
                <div>
                  <div className="text-[11.5px] font-bold text-[#1a1a1a]">{isBn ? "রোগী ইনটেক" : "Patient Intake"}</div>
                  <div className="text-[10px] text-[#9a9a9a]">{isBn ? "৪টি ফর্ম" : "4 pending forms"}</div>
                </div>
              </div>

              {/* floating mint overlay — bottom left */}
              <div className="absolute -bottom-4 -left-4 md:-left-12 bg-white p-3.5 rounded-xl shadow-[0_16px_40px_-12px_rgba(0,0,0,0.15)] border border-black/[0.04] flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-[#25c39a] animate-pulse" />
                <div className="text-[12px] font-semibold text-[#1a1a1a]">{isBn ? "সিস্টেম সিঙ্ক্রোনাইজড" : "System synchronized"}</div>
              </div>
            </div>
          </section>




          {/* ============ STATS — MINIMAL CARD ============ */}
          <section
            className="pt-16 md:pt-20 pb-4"
            aria-labelledby="stats-heading"
          >
            <h2 id="stats-heading" className="sr-only">
              {isBn ? "মূল পরিসংখ্যান" : "Key statistics"}
            </h2>
            <dl
              role="list"
              className="max-w-3xl mx-auto rounded-2xl border-t border-[#e2e8f0] bg-gradient-to-b from-[#f8fafc] to-white/60 overflow-hidden grid grid-cols-3 divide-x divide-[#e2e8f0]"
            >
              {T.stats.slice(0, 3).map((s, i) => {
                const Icon = s.icon;
                const value = t(lang, s.value);
                const label = t(lang, s.label);
                return (
                  <div
                    key={i}
                    role="listitem"
                    tabIndex={0}
                    aria-label={`${value} ${label}${s.live ? (isBn ? " · লাইভ" : " · live") : ""}`}
                    className="group px-1.5 py-3 sm:py-3.5 md:py-4 text-center outline-none focus-visible:ring-2 focus-visible:ring-[#1ba179] focus-visible:ring-offset-2 focus-visible:ring-offset-white rounded-md"
                  >
                    <div className="mx-auto mb-1.5 sm:mb-2 relative w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center" aria-hidden="true">
                      <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-[#1ba179] to-[#25c39a] shadow-[0_6px_16px_-6px_rgba(37,195,154,0.55)] transition-transform duration-300 group-hover:scale-110" />
                      <Icon className="relative w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" strokeWidth={2.4} />
                      {s.live && (
                        <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500 border border-white" />
                        </span>
                      )}
                    </div>
                    <dd
                      className="leading-none tracking-[-0.03em] font-semibold bg-gradient-to-br from-[#0f172a] to-[#1ba179] bg-clip-text text-transparent truncate"
                      style={{ fontFamily: displayFont, fontSize: "clamp(15px, 3.2vw, 22px)" }}
                    >
                      {value}
                    </dd>
                    <dt
                      className="mt-1 sm:mt-1.5 uppercase tracking-[0.12em] text-[#475569] truncate"
                      style={{ fontSize: "clamp(8.5px, 1.05vw, 10.5px)" }}
                    >
                      {label}
                    </dt>
                  </div>
                );
              })}
            </dl>
          </section>

          {/* ============ TRUSTED BY (institutions) ============ */}
          <section className="mt-20 md:mt-28">
            <p className="text-center text-[11px] font-semibold uppercase tracking-[0.24em] text-[#64748b] mb-8">
              {isBn
                ? "বাংলাদেশের চিকিৎসকদের কাছে বিশ্বস্ত"
                : "Trusted by clinicians across Bangladesh"}
            </p>
            <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-x-6 gap-y-6 items-center">
              {[
                { short: "BMDC", full: "Verified" },
                { short: "DGHS", full: "Aligned" },
                { short: "BSMMU", full: "Fellows" },
                { short: "Square", full: "Hospital" },
                { short: "Evercare", full: "Network" },
                { short: "Popular", full: "Diagnostic" },
              ].map((b) => (
                <div
                  key={b.short}
                  className="group flex items-center justify-center gap-1.5 text-[#64748b] hover:text-[#0f172a] transition-colors"
                >
                  <ShieldCheck className="w-3.5 h-3.5 text-[#1ba179] opacity-60 group-hover:opacity-100 transition" />
                  <span
                    className="text-[15px] md:text-[17px] font-semibold tracking-tight"
                    style={{ fontFamily: displayFont }}
                  >
                    {b.short}
                  </span>
                  <span className="text-[11px] text-[#94a3b8]">{b.full}</span>
                </div>
              ))}
            </div>
          </section>

          {/* ============ TRUST + INTEGRATIONS + COMPLIANCE BADGES ============ */}
          <TrustAndIntegrations isBn={isBn} displayFont={displayFont} />

          {/* ============ INTEGRATIONS SETUP GUIDES ============ */}
          <IntegrationsSetup isBn={isBn} displayFont={displayFont} bodyFont={bodyFont} />



          {/* ============ RESULTS STRIP ============ */}
          <section className="mt-20 md:mt-28">
            <div className="rounded-3xl border border-[#e2e8f0] bg-white overflow-hidden shadow-[0_20px_50px_-30px_rgba(37,195,154,0.15)]">
              <div className="grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-[#e2e8f0]">
                {[
                  {
                    k: isBn ? "৩০ সেকেন্ডে" : "30 sec",
                    l: isBn ? "গড় Rx সময়" : "avg. prescription time",
                  },
                  {
                    k: isBn ? "৪x দ্রুত" : "4× faster",
                    l: isBn ? "রোগী চেক-ইন" : "patient check-in",
                  },
                  {
                    k: "99.9%",
                    l: isBn ? "আপটাইম SLA" : "uptime · last 90 days",
                  },
                  {
                    k: "AES-256",
                    l: isBn ? "এনক্রিপ্টেড ডেটা" : "at-rest encryption",
                  },
                ].map((s, i) => (
                  <div key={i} className="p-6 md:p-8 text-center md:text-left group">
                    <div
                      className="text-[28px] md:text-[34px] leading-none tracking-[-0.03em] font-extrabold text-[#0f172a] group-hover:text-[#1ba179] transition-colors"
                      style={{ fontFamily: displayFont }}
                    >
                      {s.k}
                    </div>
                    <p className="mt-2 text-[12.5px] text-[#64748b] leading-tight">{s.l}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t border-[#e2e8f0] bg-[#f8fafc] px-6 py-3 text-[11px] text-[#64748b]">
                <span className="uppercase tracking-widest font-semibold">
                  {isBn ? "উল্লেখিত হয়েছে" : "As featured in"}
                </span>
                {["The Daily Star", "Prothom Alo", "Dhaka Tribune", "TechCrunch BD"].map((m) => (
                  <span
                    key={m}
                    className="font-semibold text-[#0f172a] tracking-tight"
                    style={{ fontFamily: displayFont }}
                  >
                    {m}
                  </span>
                ))}
              </div>
            </div>
          </section>

        </main>







        {/* ============ FEATURES ============ */}
        <section id="features" className="max-w-7xl mx-auto mt-24 md:mt-40">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="flex justify-center">
              <Kicker>{t(lang, T.features.kicker)}</Kicker>
            </div>
            <h2 className="text-4xl md:text-6xl leading-[1.05]" style={{ fontFamily: displayFont }}>
              <Serif italic={false}>{t(lang, T.features.title)}</Serif>
            </h2>
            <p className="mt-6 text-[15px] text-[#64748b] leading-relaxed">{t(lang, T.features.sub)}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {T.features.items.map((f, i) => {
              const [title, desc] = f[lang];
              const Icon = f.icon;
              return (
                <div
                  key={title}
                  className="group p-8 border border-[#e2e8f0] rounded-2xl bg-white hover:border-[#1ba179]/40 transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-12 h-12 rounded-xl bg-[#f8fafc] flex items-center justify-center text-[#1ba179] group-hover:bg-[#1ba179]/10 transition-colors border border-[#e2e8f0]">
                      <Icon className="w-5 h-5" strokeWidth={1.75} />
                    </div>
                    <span
                      className="text-[13px] text-[#64748b]"
                      style={{ fontFamily: displayFont, fontStyle: "normal" }}
                    >
                      {isBn ? ["০১","০২","০৩","০৪","০৫","০৬","০৭","০৮","০৯","১০","১১","১২"][i] : String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3
                    className="text-[22px] mb-2 text-[#111827] leading-tight"
                    style={{ fontFamily: displayFont, fontStyle: "normal" }}
                  >
                    {title}
                  </h3>
                  <p className="text-[13.5px] text-[#64748b] leading-relaxed">{desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ============ AI & GROWTH SUITE ============ */}
        <section id="ai-suite" className="max-w-7xl mx-auto mt-24 md:mt-40">
          <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] p-8 md:p-16 border border-[#0f172a]">
            <div className="absolute inset-0 opacity-30 pointer-events-none"
              style={{
                backgroundImage: "radial-gradient(circle at 20% 20%, rgba(37,195,154,0.35), transparent 40%), radial-gradient(circle at 80% 80%, rgba(56,189,248,0.25), transparent 45%)",
              }}
            />
            <div className="relative">
              <div className="text-center max-w-2xl mx-auto mb-14">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-white/80 text-[11px] font-semibold uppercase tracking-wider mb-6">
                  <Sparkles className="w-3.5 h-3.5" /> {t(lang, T.aiSuite.kicker)}
                </div>
                <h2 className="text-4xl md:text-6xl leading-[1.05] text-white" style={{ fontFamily: displayFont }}>
                  <Serif italic={false}>{t(lang, T.aiSuite.title)}</Serif>
                </h2>
                <p className="mt-6 text-[15px] text-white/70 leading-relaxed">{t(lang, T.aiSuite.sub)}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {T.aiSuite.items.map((f) => {
                  const [title, desc] = f[lang];
                  const Icon = f.icon;
                  return (
                    <Link
                      key={title}
                      to={`/ai/${f.slug}`}
                      className="group relative p-6 rounded-2xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-[#38bdf8]/40 transition-all duration-300 block"
                    >
                      <div className="flex items-center justify-between mb-5">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#1ba179] to-[#38bdf8] flex items-center justify-center text-white shadow-lg shadow-[#1ba179]/30">
                          <Icon className="w-5 h-5" strokeWidth={2} />
                        </div>
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-white/50 px-2 py-1 rounded-full border border-white/10">
                          {t(lang, f.tag)}
                        </span>
                      </div>
                      <h3 className="text-[17px] mb-2 text-white leading-tight font-semibold" style={{ fontFamily: displayFont }}>
                        {title}
                      </h3>
                      <p className="text-[13px] text-white/60 leading-relaxed">{desc}</p>
                      <div className="mt-4 inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#38bdf8] opacity-0 group-hover:opacity-100 transition">
                        Explore <ArrowRight className="w-3 h-3" />
                      </div>
                    </Link>
                  );
                })}
              </div>

              <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                <Link to="/auth" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#0f172a] text-[13px] font-semibold hover:bg-white/90 transition">
                  {isBn ? "AI সুইট চেষ্টা করুন" : "Try the AI Suite"} <ArrowRight className="w-4 h-4" />
                </Link>
                <a href="#workflow" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white/80 border border-white/20 text-[13px] font-semibold hover:bg-white/5 transition">
                  {isBn ? "কীভাবে কাজ করে দেখুন" : "See how it works"}
                </a>
              </div>
            </div>
          </div>
        </section>

        <ProductTour />

        <LivePreviews />

        <section id="workflow" className="max-w-7xl mx-auto mt-24 md:mt-40">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="flex justify-center">
              <Kicker>{t(lang, T.workflow.kicker)}</Kicker>
            </div>
            <h2 className="text-4xl md:text-6xl leading-[1.05]" style={{ fontFamily: displayFont }}>
              <Serif italic>{t(lang, T.workflow.title)}</Serif>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 relative">
            {T.workflow.steps.map((s, i) => {
              const [title, desc] = s[lang];
              return (
                <div
                  key={i}
                  className="bg-white border border-gray-200 rounded-2xl p-7 relative"
                >
                  <div className="flex items-center justify-between mb-6">
                    <div
                      className="w-11 h-11 rounded-full bg-gray-100 text-[#111827] border border-gray-200 flex items-center justify-center text-[18px]"
                      style={{ fontFamily: displayFont, fontStyle: "normal" }}
                    >
                      {isBn ? ["১","২","৩","৪"][i] : i + 1}
                    </div>
                    {i < 3 && <ArrowRight className="hidden md:block w-4 h-4 text-gray-300" />}
                  </div>
                  <h3
                    className="text-[22px] mb-2 text-[#0f172a] leading-tight"
                    style={{ fontFamily: displayFont, fontStyle: "normal" }}
                  >
                    {title}
                  </h3>
                  <p className="text-[13px] text-[#64748b] leading-relaxed">{desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ============ TRUST BAND ============ */}
        <section className="max-w-7xl mx-auto mt-24 md:mt-40 relative rounded-[2rem] overflow-hidden border border-[#0f172a]/10 min-h-[380px]">
          <img
            src={teamImg}
            alt={isBn ? "DoctorsX ব্যবহারকারী চিকিৎসক দল" : "Doctors using DoctorsX"}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            width={1280}
            height={896}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/96 via-white/88 to-white/60" />
          <div className="relative p-8 md:p-16 max-w-2xl text-[#0f172a]">
            <div className="inline-flex items-center gap-2 mb-6">
              <span className="w-8 h-px bg-blue-200" />
              <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#1ba179]">
                {isBn ? "নিরাপদ ও বিশ্বাসযোগ্য" : "Trusted & secure"}
              </span>
            </div>
            <h2 className="text-4xl md:text-6xl leading-[1.05] mb-6" style={{ fontFamily: displayFont }}>
              <Serif italic>{isBn ? "৫০০+ চিকিৎসকের নির্ভরতা।" : "Built on the trust of 500+ doctors."}</Serif>
            </h2>
            <p className="text-[15px] text-[#64748b] max-w-lg leading-relaxed mb-8">
              {isBn
                ? "এনক্রিপ্টেড ডেটা, বিএমডিসি-যাচাইকৃত সিগনেচার, এবং বাংলাদেশের সেরা হাসপাতালের চিকিৎসকদের দ্বারা প্রতিদিন ব্যবহৃত।"
                : "End-to-end encryption, BMDC-verified signatures, and used daily by specialists at Bangladesh's leading hospitals."}
            </p>
            <div className="flex flex-wrap gap-3">
              {[
                { icon: ShieldCheck, en: "HIPAA-ready", bn: "HIPAA-প্রস্তুত" },
                { icon: Award, en: "BMDC verified", bn: "বিএমডিসি যাচাইকৃত" },
                { icon: HeartPulse, en: "99.9% uptime", bn: "৯৯.৯% আপটাইম" },
              ].map((b, i) => {
                const Icon = b.icon;
                return (
                  <div key={i} className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-gray-200">
                    <Icon className="w-4 h-4 text-[#1ba179]" />
                    <span className="text-[12px] font-semibold text-[#0f172a]">{isBn ? b.bn : b.en}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ============ LIVE DEMO ============ */}
        {(() => {
          const demoSteps = [
            {
              tag: "01",
              img: demoDashboard.url,
              title: { en: "Command your day", bn: "দিনের কমান্ড সেন্টার" },
              body: {
                en: "Open DoctorsX and see today's patients, prescriptions, appointments and income — one calm overview, fully bilingual.",
                bn: "আজকের রোগী, প্রেসক্রিপশন, অ্যাপয়েন্টমেন্ট ও আয় — একটাই শান্ত ওভারভিউ, সম্পূর্ণ দ্বিভাষিক।",
              },
              bullets: {
                en: ["16 active patients", "15 prescriptions issued", "৳ 23,150 today"],
                bn: ["১৬ জন সক্রিয় রোগী", "১৫টি প্রেসক্রিপশন", "৳ ২৩,১৫০ আজ"],
              },
            },
            {
              tag: "02",
              img: demoPatients.url,
              title: { en: "Find any patient, instantly", bn: "যেকোনো রোগী খুঁজুন, সাথে সাথে" },
              body: {
                en: "Search by name, phone or condition. Every record shows age, blood group, contact and the last visit at a glance.",
                bn: "নাম, ফোন বা রোগ দিয়ে সার্চ করুন। প্রতিটি রেকর্ডে বয়স, ব্লাড গ্রুপ ও শেষ ভিজিট এক নজরে।",
              },
              bullets: {
                en: ["Bilingual patient list", "Chronic conditions tagged", "One-click history"],
                bn: ["দ্বিভাষিক রোগী তালিকা", "ক্রনিক কন্ডিশন ট্যাগ", "এক-ক্লিকে ইতিহাস"],
              },
            },
            {
              tag: "03",
              img: demoCompose.url,
              title: { en: "Write an Rx in 30 seconds", bn: "৩০ সেকেন্ডে Rx লিখুন" },
              body: {
                en: "Pick a template, use smart quick-add (napa 500 1+0+1 5d) and the meds, dose and duration are auto-parsed.",
                bn: "টেমপ্লেট নিন, স্মার্ট কুইক-অ্যাড (napa 500 1+0+1 5d) — ওষুধ, ডোজ ও সময়কাল স্বয়ংক্রিয়।",
              },
              bullets: {
                en: ["10+ Rx templates", "AI diagnosis assist", "Save & PDF in one click"],
                bn: ["১০+ Rx টেমপ্লেট", "AI ডায়াগনসিস", "এক-ক্লিকে PDF"],
              },
            },
            {
              tag: "04",
              img: demoPrescriptions.url,
              title: { en: "Share, print, follow up", bn: "শেয়ার, প্রিন্ট, ফলো-আপ" },
              body: {
                en: "Every prescription is stored, searchable and shareable via QR, PDF or a direct patient link — plus WhatsApp follow-up.",
                bn: "প্রতিটি Rx সংরক্ষিত ও সার্চযোগ্য — QR, PDF বা লিংকে শেয়ার এবং হোয়াটসঅ্যাপ ফলো-আপ।",
              },
              bullets: {
                en: ["QR share to patient", "Export bilingual PDF", "WhatsApp reminder"],
                bn: ["রোগীকে QR শেয়ার", "দ্বিভাষিক PDF", "হোয়াটসঅ্যাপ রিমাইন্ডার"],
              },
            },
          ];
          const step = demoSteps[demoStep];
          return (
            <section id="live-demo" className="max-w-7xl mx-auto mt-24 md:mt-40">
              <div className="text-center max-w-2xl mx-auto mb-14">
                <div className="flex justify-center">
                  <Kicker>{isBn ? "লাইভ ডেমো" : "Live product tour"}</Kicker>
                </div>
                <h2 className="text-4xl md:text-6xl leading-[1.05]" style={{ fontFamily: displayFont }}>
                  <Serif italic>
                    {isBn ? "৪ ধাপে সম্পূর্ণ ওয়ার্কফ্লো।" : "The full workflow, in four screens."}
                  </Serif>
                </h2>
                <p className="mt-5 text-[15px] text-[#64748b] max-w-lg mx-auto">
                  {isBn
                    ? "রিয়েল স্ক্রিনশট, রিয়েল ডেটা — DoctorsX-এ চেম্বার চালানো ঠিক এভাবেই।"
                    : "Real screenshots, real data — this is exactly how a chamber runs on DoctorsX."}
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
                {/* Steps rail */}
                <div className="lg:col-span-4 flex flex-col gap-3">
                  {demoSteps.map((s, i) => {
                    const active = i === demoStep;
                    return (
                      <button
                        key={s.tag}
                        onClick={() => setDemoStep(i)}
                        className={`text-left rounded-2xl p-5 border transition-all ${
                          active
                            ? "bg-white text-[#0f172a] border-blue-200 shadow-sm"
                            : "bg-[#f8fafc] text-[#0f172a] border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className={`text-[11px] font-mono tracking-wider px-2 py-0.5 rounded-full border ${
                              active ? "border-blue-200 text-[#1ba179]" : "border-[#0f172a]/15 text-[#1ba179]"
                            }`}
                          >
                            {s.tag}
                          </span>
                          <h3
                            className="text-[17px] leading-tight"
                            style={{ fontFamily: displayFont, fontStyle: "normal" }}
                          >
                            {t(lang, s.title)}
                          </h3>
                        </div>
                        <p className="mt-2 text-[13px] leading-relaxed text-[#64748b]">
                          {t(lang, s.body)}
                        </p>
                        {active && (
                          <ul className="mt-4 space-y-1.5">
                            {(isBn ? s.bullets.bn : s.bullets.en).map((b) => (
                              <li key={b} className="flex items-center gap-2 text-[12px] text-[#374151]">
                                <Check className="w-3.5 h-3.5 text-[#1ba179]" strokeWidth={2.5} />
                                {b}
                              </li>
                            ))}
                          </ul>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Screenshot frame */}
                <div className="lg:col-span-8">
                  <div className="relative bg-white rounded-3xl border border-[#0f172a]/10 shadow-[0_4px_20px_-6px_rgba(0,0,0,0.08)] overflow-hidden">
                    {/* Mac-style titlebar */}
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-[#0f172a]/8 bg-[#f8fafc]">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                      <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                      <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                      <div className="mx-auto flex items-center gap-2 px-3 py-1 rounded-md bg-white border border-[#0f172a]/10 text-[11px] text-[#1ba179]">
                        <Lock className="w-3 h-3" />
                        app.doctorsx.com{step.tag === "01" ? "/app" : step.tag === "02" ? "/patients" : step.tag === "03" ? "/prescriptions/new" : "/prescriptions"}
                      </div>
                      <span className="text-[10px] font-mono text-[#1ba179]/60">Step {step.tag} / 04</span>
                    </div>
                    <div className="relative bg-[#f8fafc]">
                      <img
                        key={step.img}
                        src={step.img}
                        alt={t(lang, step.title)}
                        className="w-full h-auto block animate-in fade-in duration-500"
                        loading="lazy"
                      />
                      {/* Floating step chip */}
                      <div className="absolute top-4 right-4 bg-white text-[#0f172a] text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[#0f172a]/10 flex items-center gap-2 shadow-sm">
                        <Sparkles className="w-3 h-3 text-[#1ba179]" />
                        {isBn ? "লাইভ প্রিভিউ" : "Live preview"}
                      </div>
                    </div>
                  </div>

                  {/* progress bar */}
                  <div className="mt-4 flex items-center gap-2">
                    {demoSteps.map((_, i) => (
                      <button
                        key={i}
                        aria-label={`Go to step ${i + 1}`}
                        onClick={() => setDemoStep(i)}
                        className={`h-1.5 rounded-full transition-all ${
                          i === demoStep ? "w-10 bg-[#111827]" : "w-4 bg-gray-200 hover:bg-gray-300"
                        }`}
                      />
                    ))}
                    <span className="ml-auto text-[11px] text-[#64748b]">
                      {isBn ? "স্টেপে ক্লিক করে বাকিটা দেখুন" : "Click a step to advance the tour"}
                    </span>
                  </div>

                  {/* CTA */}
                  <button
                    onClick={scrollToPricingAndOpenSignup}
                    className="mt-6 w-full bg-[#111827] text-white rounded-xl py-3.5 font-bold text-[14px] hover:bg-[#1ba179] transition inline-flex items-center justify-center gap-2 shadow-[0_4px_14px_-4px_rgba(37,195,154,0.25)]"
                  >
                    {isBn ? "ফ্রি ট্রায়াল শুরু করুন — প্রাইসিং দেখুন" : "Start free trial — view pricing"}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Feature highlights grid */}
              <div className="mt-16">
                <div className="text-center max-w-2xl mx-auto mb-10">
                  <div className="flex justify-center">
                    <Kicker>{t(lang, T.liveDemo.highlights.kicker)}</Kicker>
                  </div>
                  <h3 className="text-3xl md:text-4xl leading-[1.1]" style={{ fontFamily: displayFont }}>
                    <Serif italic>{t(lang, T.liveDemo.highlights.title)}</Serif>
                  </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {T.liveDemo.highlights.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <div
                        key={item.en[0]}
                        className="group rounded-2xl p-6 bg-white border border-[#0f172a]/10 hover:border-[#1ba179]/50 transition-all hover:shadow-[0_18px_40px_-22px_rgba(6,78,59,0.18)]"
                      >
                        <div className="w-10 h-10 rounded-xl bg-[#f8fafc] border border-[#0f172a]/10 flex items-center justify-center mb-4 group-hover:bg-[#0f172a] group-hover:text-[#f8fafc] transition-colors">
                          <Icon className="w-5 h-5 text-[#1ba179] group-hover:text-[#f8fafc]" strokeWidth={2} />
                        </div>
                        <h4 className="text-[16px] font-semibold text-[#0f172a] mb-1.5" style={{ fontFamily: displayFont }}>
                          {isBn ? item.bn[0] : item.en[0]}
                        </h4>
                        <p className="text-[13px] leading-relaxed text-[#64748b]">
                          {isBn ? item.bn[1] : item.en[1]}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>


          );
        })()}

        {/* ============ WEBSITE BUILDER ============ */}
        <section id="website-builder" className="max-w-7xl mx-auto mt-24 md:mt-40">
          <div className="rounded-3xl bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] p-8 md:p-14 text-[#f8fafc] overflow-hidden relative">
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#1ba179]/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#25c39a]/10 rounded-full blur-3xl" />

            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-3 py-1 text-[11px] font-semibold uppercase tracking-wider mb-5">
                  <Sparkles className="w-3.5 h-3.5" />
                  {isBn ? "নতুন — ওয়েবসাইট বিল্ডার" : "New — Website Builder"}
                </div>
                <h2 className="text-4xl md:text-5xl leading-[1.05] font-bold" style={{ fontFamily: displayFont, letterSpacing: "-0.02em" }}>
                  {isBn
                    ? "কোড ছাড়াই আপনার নিজস্ব ডাক্তারি ওয়েবসাইট"
                    : "Your own doctor website — no code required"}
                </h2>
                <p className="mt-4 text-[15px] leading-relaxed text-white/70 max-w-lg">
                  {isBn
                    ? "প্রিবিল্ট টেমপ্লেট বেছে নিন, কনটেন্ট ও রং বদলান, আপনার কাস্টম ডোমেইন কানেক্ট করুন — সবকিছু আপনার ড্যাশবোর্ড থেকেই।"
                    : "Pick a prebuilt template, edit your content and colors, then connect your custom domain — all from inside your dashboard."}
                </p>

                <ul className="mt-6 space-y-3">
                  {[
                    { en: "4 distinct themes — Classic, Modern, Minimal, Bold", bn: "৪টি আলাদা থিম — ক্লাসিক, মডার্ন, মিনিমাল, বোল্ড" },
                    { en: "Live content editor for hero, services, about, contact", bn: "হিরো, সেবা, সম্পর্কে, যোগাযোগের জন্য লাইভ এডিটর" },
                    { en: "Bring your own domain with 1-click DNS verification", bn: "আপনার নিজের ডোমেইন কানেক্ট করুন" },
                    { en: "Bilingual-ready and mobile-first out of the box", bn: "দ্বিভাষিক ও মোবাইল-ফার্স্ট প্রস্তুত" },
                  ].map((item) => (
                    <li key={item.en} className="flex items-start gap-3 text-[14px] text-white/85">
                      <div className="mt-0.5 h-5 w-5 rounded-full bg-[#1ba179]/20 flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-[#4de3b8]" strokeWidth={3} />
                      </div>
                      <span>{isBn ? item.bn : item.en}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/website"
                    className="inline-flex items-center gap-2 bg-[#1ba179] hover:bg-[#0f8f6b] text-white px-5 py-2.5 rounded-full text-[14px] font-semibold transition-colors"
                  >
                    {isBn ? "ওয়েবসাইট তৈরি করুন" : "Build your website"}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <a
                    href="#pricing"
                    className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 backdrop-blur text-white px-5 py-2.5 rounded-full text-[14px] font-semibold transition-colors"
                  >
                    {isBn ? "প্রাইসিং দেখুন" : "See pricing"}
                  </a>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { name: "Classic", palette: ["#7c2d12", "#f5f5f4", "#a16207"], font: "serif" },
                  { name: "Modern", palette: ["#1ba179", "#f8fafc", "#25c39a"], font: "sans" },
                  { name: "Minimal", palette: ["#111827", "#ffffff", "#6b7280"], font: "mono" },
                  { name: "Bold", palette: ["#7c3aed", "#0f172a", "#f472b6"], font: "sans" },
                ].map((tpl) => (
                  <div
                    key={tpl.name}
                    className="group rounded-2xl bg-white/[0.04] border border-white/10 p-4 hover:bg-white/[0.08] hover:border-white/20 transition-all cursor-default"
                  >
                    <div className="aspect-[4/3] rounded-lg overflow-hidden mb-3 flex flex-col" style={{ backgroundColor: tpl.palette[1] }}>
                      <div className="h-1/3 flex items-center px-2.5 gap-1" style={{ backgroundColor: tpl.palette[0] }}>
                        <div className="w-1.5 h-1.5 rounded-full bg-white/50" />
                        <div className="w-1.5 h-1.5 rounded-full bg-white/50" />
                        <div className="w-1.5 h-1.5 rounded-full bg-white/50" />
                      </div>
                      <div className="flex-1 p-2 space-y-1">
                        <div className="h-1.5 w-2/3 rounded" style={{ backgroundColor: tpl.palette[0], opacity: 0.7 }} />
                        <div className="h-1 w-full rounded bg-black/10" />
                        <div className="h-1 w-4/5 rounded bg-black/10" />
                        <div className="mt-1.5 h-2 w-16 rounded" style={{ backgroundColor: tpl.palette[2] }} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-[13px] font-semibold">{tpl.name}</p>
                      <div className="flex gap-1">
                        {tpl.palette.map((c) => (
                          <div key={c} className="w-3 h-3 rounded-full border border-white/20" style={{ backgroundColor: c }} />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>


        <section id="reviews" className="max-w-7xl mx-auto mt-24 md:mt-40">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="flex justify-center">
              <Kicker>{t(lang, T.reviews.kicker)}</Kicker>
            </div>
            <h2 className="text-4xl md:text-6xl leading-[1.05]" style={{ fontFamily: displayFont, fontWeight: 800, letterSpacing: "-0.02em" }}>
              {t(lang, T.reviews.title)}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {T.reviews.items.map((r, i) => {
              const featured = i === 1;
              return (
                <figure
                  key={r.name.en}
                  className={`group rounded-2xl p-6 flex flex-col border transition-all h-full ${
                    featured
                      ? "bg-white text-[#0f172a] border-blue-200 ring-1 ring-blue-100 shadow-sm"
                      : "bg-white text-[#0f172a] border-gray-200 hover:border-gray-300 hover:shadow-sm"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <Quote className="w-6 h-6 text-gray-200 group-hover:text-gray-300 transition-colors" strokeWidth={2.5} />
                    <div className="flex gap-0.5 text-[#1ba179]">
                      {Array.from({ length: 5 }).map((_, si) => <Star key={si} className="w-3.5 h-3.5 fill-current" />)}
                    </div>
                  </div>
                  <blockquote
                    className="mt-4 text-[17px] leading-snug flex-grow"
                    style={{ fontFamily: displayFont, fontWeight: 500, fontStyle: "normal" }}
                  >
                    "{t(lang, r.quote)}"
                  </blockquote>
                  <figcaption className="mt-6 pt-5 border-t border-[#0f172a]/10 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-[13px] font-bold shrink-0 ${
                      featured ? "bg-[#0f172a] text-[#f8fafc]" : "bg-gray-50 text-[#111827] border border-gray-200"
                    }`}>
                      {t(lang, r.name).split(" ").slice(-1)[0][0]}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold text-[#0f172a] truncate">{t(lang, r.name)}</p>
                      <p className="text-[11px] text-[#64748b] truncate">{t(lang, r.role)}</p>
                    </div>
                  </figcaption>
                </figure>
              );
            })}
          </div>
        </section>

        {/* ============ INTEGRATIONS ============ */}
        <section id="integrations" className="max-w-7xl mx-auto mt-24 md:mt-40">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="flex justify-center">
              <Kicker>{isBn ? "ইন্টিগ্রেশন" : "Integrations"}</Kicker>
            </div>
            <h2 className="text-4xl md:text-6xl leading-[1.05]" style={{ fontFamily: displayFont }}>
              <Serif italic>{isBn ? "আপনার চেনা টুলগুলোর সাথে।" : "Works with the tools you already use."}</Serif>
            </h2>
            <p className="mt-5 text-[15px] text-[#64748b] max-w-lg mx-auto">
              {isBn
                ? "পেমেন্ট, মেসেজিং ও রেকর্ড — বাংলাদেশী প্র্যাকটিসের জন্য প্রি-বিল্ট।"
                : "Payments, messaging and records — pre-wired for a Bangladeshi practice."}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { name: "bKash", tag: isBn ? "পেমেন্ট" : "Payments", color: "#e2136e" },
              { name: "Nagad", tag: isBn ? "পেমেন্ট" : "Payments", color: "#ec1c24" },
              { name: "WhatsApp", tag: isBn ? "মেসেজিং" : "Messaging", color: "#25d366" },
              { name: "SSLCommerz", tag: isBn ? "কার্ড" : "Cards", color: "#1d3f77" },
              { name: "Twilio SMS", tag: "SMS", color: "#f22f46" },
              { name: "Google Calendar", tag: isBn ? "সময়সূচি" : "Schedule", color: "#4285f4" },
            ].map((it) => (
              <div
                key={it.name}
                className="group bg-white border border-gray-200 rounded-2xl p-5 flex flex-col items-start hover:border-gray-300 hover:-translate-y-0.5 transition-all"
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-[13px] font-black mb-4"
                  style={{ backgroundColor: it.color, fontFamily: bodyFont }}
                >
                  {it.name[0]}
                </div>
                <p className="text-[14px] font-semibold text-[#0f172a]">{it.name}</p>
                <p className="text-[11px] text-[#64748b] mt-0.5">{it.tag}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-2 text-[12px] text-[#64748b]">
            <Sparkles className="w-3.5 h-3.5 text-[#9ca3af]" />
            {isBn ? "শীঘ্রই আসছে: Zoom, Google Meet, CSV এক্সপোর্ট, Zapier।" : "Coming soon: Zoom, Google Meet, CSV export, Zapier."}
          </div>
        </section>

        {/* ============ SECURITY & COMPLIANCE ============ */}
        <section id="security" className="max-w-7xl mx-auto mt-24 md:mt-40">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left column — headline */}
            <div className="lg:col-span-5 bg-white text-[#111827] rounded-3xl p-10 md:p-12 border border-gray-200">
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-50 border border-gray-200 text-[11px] uppercase tracking-[0.14em] text-[#1ba179]">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  {isBn ? "সিকিউরিটি ও কমপ্লায়েন্স" : "Security & Compliance"}
                </div>
                <h2 className="mt-6 text-4xl md:text-5xl leading-[1.05]" style={{ fontFamily: displayFont }}>
                  <Serif italic>
                    {isBn ? "রোগীর তথ্য — হাসপাতাল-গ্রেড সুরক্ষায়।" : "Patient data, hospital-grade safe."}
                  </Serif>
                </h2>
                <p className="mt-6 text-[15px] text-[#64748b] leading-relaxed">
                  {isBn
                    ? "প্রতিটি রেকর্ড এনক্রিপ্টেড। প্রতিটি চিকিৎসকের ডেটা সম্পূর্ণ পৃথক। প্রতিদিন অটো-ব্যাকআপ।"
                    : "Every record is encrypted. Every doctor's data is fully isolated. Automatic backups every single day."}
                </p>
                <div className="mt-8 flex flex-wrap gap-2">
                  {["HIPAA-aligned", "BMDC verified", "ISO 27001 practices", "GDPR-ready"].map((b) => (
                    <span key={b} className="text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full bg-gray-50 border border-gray-200 text-[#111827]">
                      {b}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Right column — 4 pillars grid */}
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: Lock, en: ["End-to-end encryption", "TLS 1.3 in transit, AES-256 at rest — from your browser to our database."], bn: ["এন্ড-টু-এন্ড এনক্রিপশন", "TLS 1.3 ট্রান্সিটে, AES-256 রেস্টে — ব্রাউজার থেকে ডাটাবেস পর্যন্ত।"] },
                { icon: Database, en: ["Row-level isolation", "Each doctor's records are enforced separate at the database level."], bn: ["Row-level আইসোলেশন", "প্রতি চিকিৎসকের রেকর্ড ডাটাবেস লেভেলে পৃথক।"] },
                { icon: KeyRound, en: ["Role-based access", "Doctor, staff, and patient views with fine-grained permissions."], bn: ["রোল-ভিত্তিক অ্যাক্সেস", "চিকিৎসক, স্টাফ ও রোগীর জন্য পৃথক অনুমতি।"] },
                { icon: Server, en: ["Daily encrypted backups", "Point-in-time recovery for the last 30 days, tested weekly."], bn: ["ডেইলি এনক্রিপ্টেড ব্যাকআপ", "৩০ দিনের রিকভারি, সাপ্তাহিক টেস্টেড।"] },
                { icon: FileCheck2, en: ["Audit logs", "Every prescription, invoice and record change is logged and searchable."], bn: ["অডিট লগ", "প্রতিটি Rx, ইনভয়েস ও রেকর্ড পরিবর্তন লগ করা।"] },
                { icon: Cloud, en: ["99.9% uptime SLA", "Redundant infrastructure with automatic failover across regions."], bn: ["৯৯.৯% আপটাইম SLA", "রিডান্ডেন্ট ইনফ্রা ও অটো-ফেলওভার।"] },
              ].map((p) => {
                const [title, desc] = isBn ? p.bn : p.en;
                return (
                  <div
                    key={title}
                    className="bg-white border border-gray-200 rounded-2xl p-6 hover:border-gray-300 hover:-translate-y-0.5 transition-all"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#f8fafc] flex items-center justify-center text-[#1ba179] mb-4">
                      <p.icon className="w-5 h-5" strokeWidth={2} />
                    </div>
                    <h3 className="text-[15px] font-semibold text-[#0f172a] mb-1.5">{title}</h3>
                    <p className="text-[13px] leading-relaxed text-[#64748b]">{desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ============ ROI + COMPARISON ============ */}
        <RoiAndCompare isBn={isBn} displayFont={displayFont} bodyFont={bodyFont} />

        {/* ============ TESTIMONIALS CAROUSEL ============ */}
        <div className="max-w-7xl mx-auto mt-24 px-4">
          <TestimonialsCarousel variant="dark" />
        </div>

        {/* ============ TESTIMONIALS ============ */}
        <Testimonials isBn={isBn} displayFont={displayFont} bodyFont={bodyFont} />

        {/* ============ CASE STUDIES ============ */}
        <CaseStudies isBn={isBn} displayFont={displayFont} bodyFont={bodyFont} />






        {/* ============ PRICING ============ */}
        <section
          id="pricing"
          className="max-w-7xl mx-auto mt-24 md:mt-40 bg-gray-50 border border-gray-200 rounded-[2.5rem] p-8 md:p-16"
        >
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="flex justify-center">
              <Kicker>{t(lang, T.pricing.kicker)}</Kicker>
            </div>
            <h2 className="text-4xl md:text-6xl leading-[1.05] mb-5" style={{ fontFamily: displayFont }}>
              <Serif italic>{t(lang, T.pricing.title)}</Serif>
            </h2>
            <p className="text-[15px] text-[#64748b]">{t(lang, T.pricing.sub)}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {T.pricing.plans.map((p) => (
              <div
                key={p.name.en}
                className={`p-8 rounded-3xl flex flex-col relative ${
                  p.highlight
                    ? "bg-white text-[#0f172a] ring-2 ring-blue-200 md:scale-[1.03] z-10 shadow-sm"
                    : "bg-gray-50 border border-gray-200 text-[#111827]"
                }`}
              >
                {p.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#1ba179] text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider">
                    {t(lang, T.pricing.popular)}
                  </span>
                )}
                <div className={`text-[10px] font-bold uppercase tracking-[0.2em] mb-4 ${p.highlight ? "text-[#1ba179]" : "text-[#1ba179]"}`}>
                  {t(lang, p.name)}
                </div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span
                    className="text-5xl"
                    style={{ fontFamily: displayFont, fontStyle: "normal" }}
                  >
                    {(p.price.en as string) === "Custom" ? t(lang, p.price) : `৳${t(lang, p.price)}`}
                  </span>
                  {p.priceSuffix.en && (
                    <span className="text-[13px] text-[#64748b]">
                      {t(lang, p.priceSuffix)}
                    </span>
                  )}
                </div>
                <p className="text-[12.5px] mb-6 text-[#64748b]">
                  {t(lang, p.tagline)}
                </p>
                <ul className="space-y-3 mb-8 flex-1">
                  {p.features[lang].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[13.5px]">
                      <Check className="w-4 h-4 shrink-0 mt-0.5 text-[#1ba179]" />
                      <span className="text-[#374151]">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/auth"
                  className={`w-full py-3.5 rounded-xl font-bold text-[13.5px] text-center transition ${
                    p.highlight
                      ? "bg-[#1ba179] text-white hover:bg-blue-700"
                      : "border border-gray-200 hover:bg-white text-[#111827]"
                  }`}
                >
                  {t(lang, p.cta)}
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* ============ FAQ ============ */}
        <section id="faq" className="max-w-4xl mx-auto mt-24 md:mt-40">
          <div className="text-center mb-14">
            <div className="flex justify-center">
              <Kicker>FAQ</Kicker>
            </div>
            <h2 className="text-4xl md:text-6xl leading-[1.05]" style={{ fontFamily: displayFont }}>
              <Serif italic>{t(lang, T.faq.title)}</Serif>
            </h2>
          </div>
          <div className="space-y-3">
            {T.faq.items.map((item) => (
              <details key={item.q.en} className="group bg-white border border-gray-200 rounded-2xl p-6 open:bg-gray-50 transition">
                <summary className="cursor-pointer list-none flex items-center justify-between gap-6">
                  <span
                    className="text-[17px] text-[#0f172a] leading-snug"
                    style={{ fontFamily: displayFont, fontStyle: "normal" }}
                  >
                    {t(lang, item.q)}
                  </span>
                  <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-[#64748b] text-[16px] transition group-open:rotate-45 group-open:bg-white group-open:text-[#1ba179] group-open:border group-open:border-gray-200">
                    +
                  </span>
                </summary>
                <p className="mt-4 text-[13.5px] leading-relaxed text-[#64748b]">{t(lang, item.a)}</p>
              </details>
            ))}
          </div>
        </section>

        {/* ============ LIVE PULSE ============ */}
        <section className="max-w-7xl mx-auto mt-16 md:mt-24 px-4">
          <PageLivePulse accent="#1ba179" />
          <PageLiveExamples accent="#1ba179" />
        </section>

        <PortraitShowcase
          eyebrow="Reputation on autopilot"
          scene="reviews"
          portrait="doctor-female"
          title="Turn every visit into a five-star review"
          subtitle="Draft AI replies, publish across Google and Facebook, and route unhappy patients to a private feedback loop — without leaving DoctorsX."
        />
        <PortraitShowcase
          reversed
          eyebrow="Clean claims, faster"
          scene="billing"
          portrait="staff"
          title="Submit claims and reconcile payments in minutes"
          subtitle="Multi-chamber fees, bKash and Nagad reconciliation, and clean electronic claim batches ready before your coffee cools."
        />
        <PlatformShowcase />
        <IntuitiveToolsSection />
        <PortraitShowcase
          eyebrow="Prescribe with confidence"
          scene="prescribe"
          portrait="doctor-male"
          title="One tap. Bengali-ready prescription, sent."
          subtitle="Autocomplete for 50+ Bangladeshi medicines, interaction alerts, and WhatsApp delivery — all before the patient stands up."
        />
        <PortraitShowcase
          reversed
          eyebrow="Grow your online presence"
          scene="presence"
          portrait="doctor-glasses"
          title="Own how patients find you online"
          subtitle="Sync your profile to Google, Facebook and Apple Maps in one click — and keep timings, phone and services current everywhere."
        />
        <OutcomeStatsBento />
        <TrustStatBanner />
        <AudienceScaleSection />
        <FourStepsSection />

        {/* ============ FINAL CTA ============ */}
        <HowItWorks />

        {/* ============ FINAL CTA ============ */}
        <section className="max-w-7xl mx-auto mt-24 md:mt-40 bg-gray-50 rounded-[2.5rem] p-10 md:p-20 text-center text-[#111827] border border-gray-200">
          <div className="relative">
            <Sparkles className="mx-auto w-6 h-6 mb-6 text-[#1ba179]" />
            <h2 className="text-4xl md:text-7xl mb-6 leading-[1.05] max-w-3xl mx-auto" style={{ fontFamily: displayFont }}>
              <Serif italic>{t(lang, T.cta.title)}</Serif>
            </h2>
            <p className="text-[#64748b] text-[16px] mb-10 max-w-xl mx-auto">{t(lang, T.cta.sub)}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/auth"
                className="px-8 py-4 bg-[#111827] text-white rounded-2xl font-bold text-[14px] hover:bg-[#1ba179] transition inline-flex items-center gap-2 shadow-sm"
              >
                {t(lang, T.cta.primary)} <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="mailto:hello@doctorsx.app"
                className="px-8 py-4 rounded-2xl font-bold text-[14px] border border-gray-200 text-[#111827] hover:bg-gray-50 transition"
              >
                {t(lang, T.cta.secondary)}
              </a>
            </div>
          </div>
        </section>

        {/* Signup prompt dialog */}
        <Dialog open={signupOpen} onOpenChange={setSignupOpen}>
          <DialogContent className="sm:max-w-[420px] bg-white border-[#0f172a]/10">
            <DialogHeader>
              <DialogTitle className="text-[#0f172a] text-2xl" style={{ fontFamily: displayFont }}>
                {isBn ? "আপনার ডিজিটাল চেম্বার শুরু করুন" : "Start your digital chamber"}
              </DialogTitle>
              <DialogDescription className="text-[#1ba179]">
                {isBn
                  ? "১৪-দিন ফ্রি ট্রায়াল — ক্রেডিট কার্ড প্রয়োজন নয়।"
                  : "14-day free trial — no credit card required."}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSignupSubmit} className="space-y-4 mt-4">
              <div className="space-y-1.5">
                <Label htmlFor="signup-email" className="text-[#0f172a]">
                  {isBn ? "ইমেইল" : "Email"}
                </Label>
                <Input
                  id="signup-email"
                  type="email"
                  placeholder={isBn ? "আপনার ইমেইল" : "your@email.com"}
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                  required
                  className="h-11 rounded-xl border-[#0f172a]/10 focus-visible:ring-[#0f172a]"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signup-phone" className="text-[#0f172a]">
                  {isBn ? "মোবাইল নম্বর" : "Mobile number"}
                </Label>
                <Input
                  id="signup-phone"
                  type="tel"
                  placeholder={isBn ? "০১XXXXXXXXX" : "01XXXXXXXXX"}
                  value={signupPhone}
                  onChange={(e) => setSignupPhone(e.target.value)}
                  required
                  className="h-11 rounded-xl border-[#0f172a]/10 focus-visible:ring-[#0f172a]"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-[#111827] hover:bg-[#1ba179] text-white rounded-xl font-bold"
              >
                {isBn ? "ফ্রি ট্রায়াল শুরু করুন" : "Start free trial"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-[11px] text-center text-[#64748b]">
                {isBn
                  ? "ক্লিক করলে আপনি আমাদের শর্তাবলী মেনে নিচ্ছেন।"
                  : "By clicking, you agree to our Terms of Service."}
              </p>
            </form>
          </DialogContent>
        </Dialog>

        {/* ============ FAQ ============ */}
        <FaqSection isBn={isBn} displayFont={displayFont} bodyFont={bodyFont} />

        {/* ============ INTEGRATIONS ============ */}
        <div className="mt-16 -mx-4 sm:-mx-6 lg:-mx-8">
          <IntegrationsSection />
        </div>

        {/* ============ SECURITY & TRUST ============ */}
        <div className="mt-16 -mx-4 sm:-mx-6 lg:-mx-8">
          <SecurityTrustSection />
        </div>

        {/* ============ BOTTOM INVITE CTA ============ */}
        <div className="mt-8 -mx-4 sm:-mx-6 lg:-mx-8">
          <BottomInviteCta />
        </div>



        {/* ============ FOOTER ============ */}
        <footer className="max-w-7xl mx-auto mt-16 pt-12 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 pb-10">
            <div className="col-span-2">
              <Logo />
              <p className="mt-4 text-[13px] text-[#64748b] max-w-xs leading-relaxed">
                {t(lang, T.footer.tagline)}
              </p>
              <div className="mt-6 max-w-md">
                <NewsletterSignup source="footer_landing" />
              </div>
            </div>
            {[
              { title: T.footer.product, links: [[T.nav.features, "#features"], [T.nav.pricing, "#pricing"], [T.nav.workflow, "#workflow"], [T.nav.signin, "/auth"]] as const },
              { title: T.footer.company, links: [[{ en: "About", bn: "আমাদের সম্পর্কে" }, "#"], [{ en: "Blog", bn: "ব্লগ" }, "#"], [{ en: "Contact", bn: "যোগাযোগ" }, "mailto:hello@doctorsx.app"]] as const },
              { title: T.footer.legal, links: [[{ en: "Privacy", bn: "প্রাইভেসি" }, "#"], [{ en: "Terms", bn: "শর্তাবলী" }, "#"], [{ en: "Security", bn: "নিরাপত্তা" }, "#"]] as const },
            ].map((col, i) => (
              <div key={i}>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#111827] mb-4">{t(lang, col.title)}</p>
                <ul className="space-y-3">
                  {col.links.map(([label, href], j) => (
                    <li key={j}>
                      <a href={href} className="text-[13px] text-[#64748b] hover:text-[#111827] transition">
                        {t(lang, label as any)}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-200 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[12px] text-[#64748b]">
            <span>© {new Date().getFullYear()} DoctorsX. {t(lang, T.footer.rights)}</span>
            <div className="flex items-center gap-3">
              <StatusWidget lang={lang} />
              <div className="flex items-center gap-2">
                <Languages className="w-3.5 h-3.5" />
                <span>English + বাংলা</span>
              </div>
            </div>
          </div>
        </footer>
      </div>
      <ScrollToTopFab />
    </div>
  );
};

export default Landing;
